import { AlertTriangle, Info } from 'lucide-react';

import { ColorPicker } from '@/components/color-picker/ColorPicker';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

import {
  INNER_EYE_OPTIONS,
  INNER_EYE_STYLES_WITH_RADIUS,
  isScannerSafeInnerEye,
  isScannerSafeOuterEye,
  OUTER_EYE_OPTIONS,
  OUTER_EYE_STYLES_WITH_RADIUS,
} from '../config/styles';
import {
  INNER_EYE_STYLES,
  OUTER_EYE_STYLES,
  type InnerEyeStyle,
  type OuterEyeStyle,
} from '../domain/types';
import { useQrContext } from '../state/qr-context';
import { EyePreview } from './EyePreview';

function isOuterEyeStyle(value: string): value is OuterEyeStyle {
  return (OUTER_EYE_STYLES as readonly string[]).includes(value);
}

function isInnerEyeStyle(value: string): value is InnerEyeStyle {
  return (INNER_EYE_STYLES as readonly string[]).includes(value);
}

/** A 0–100 % rounding control, shown only for styles that respond to it. */
function RadiusSlider({
  id,
  label,
  value,
  onChange,
}: {
  readonly id: string;
  readonly label: string;
  readonly value: number;
  readonly onChange: (value: number) => void;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between">
        <Label htmlFor={id}>{label}</Label>
        <span className="text-sm tabular-nums text-muted-foreground">
          {Math.round(value * 100)} %
        </span>
      </div>
      <Slider
        id={id}
        value={[value]}
        min={0}
        max={1}
        step={0.05}
        onValueChange={([next]) => {
          if (next !== undefined) onChange(next);
        }}
      />
      <div className="flex justify-between text-xs text-muted-foreground">
        <span>Eckig</span>
        <span>Rund</span>
      </div>
    </div>
  );
}

export function EyeSettings() {
  const { design, actions } = useQrContext();

  const eyeShapesAreScannerSafe =
    isScannerSafeOuterEye(design.outerEyeStyle) && isScannerSafeInnerEye(design.innerEyeStyle);

  const showOuterRadius = OUTER_EYE_STYLES_WITH_RADIUS.includes(design.outerEyeStyle);
  const showInnerRadius = INNER_EYE_STYLES_WITH_RADIUS.includes(design.innerEyeStyle);

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2 border-b pb-4">
        <Switch
          id="eyes-as-modules"
          checked={design.styleEyesAsModules}
          onCheckedChange={actions.setStyleEyesAsModules}
        />
        <Label htmlFor="eyes-as-modules" className="cursor-pointer">
          Ecken im Modul-Stil zeichnen
        </Label>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button type="button" className="text-muted-foreground" aria-label="Hinweis">
                <Info className="h-4 w-4" aria-hidden="true" />
              </button>
            </TooltipTrigger>
            <TooltipContent>
              <p className="max-w-64">
                Zeichnet die drei Eckmarkierungen mit dem gewählten Modul-Stil statt als eigene
                Formen. Das kann die Erkennung deutlich verschlechtern.
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {design.styleEyesAsModules ? (
        <Alert variant="destructive">
          <AlertTriangle aria-hidden="true" />
          <AlertDescription>
            Die Eckmarkierungen sind der Ankerpunkt jedes Scanners. Im Modul-Stil gezeichnet werden
            sie von vielen Geräten nicht mehr erkannt — bitte unbedingt testen.
          </AlertDescription>
        </Alert>
      ) : (
        <div className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="outer-eye-style">Äußere Form</Label>
              <Select
                value={design.outerEyeStyle}
                onValueChange={(value) => {
                  if (isOuterEyeStyle(value)) actions.setOuterEyeStyle(value);
                }}
              >
                <SelectTrigger id="outer-eye-style">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {OUTER_EYE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="inner-eye-style">Innere Form</Label>
              <Select
                value={design.innerEyeStyle}
                onValueChange={(value) => {
                  if (isInnerEyeStyle(value)) actions.setInnerEyeStyle(value);
                }}
              >
                <SelectTrigger id="inner-eye-style">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {INNER_EYE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <ColorPicker
            label="Farbe der Ecken"
            value={design.eyeColor}
            onChange={actions.setEyeColor}
          />

          {!eyeShapesAreScannerSafe && (
            <Alert variant="warning">
              <AlertTriangle aria-hidden="true" />
              <AlertDescription>
                Diese Eckform verändert das Muster, an dem sich Scanner orientieren. Der Code sieht
                gut aus, wird aber von vielen Lesegeräten nicht erkannt — teste ihn unbedingt, oder
                bleibe bei einer der einfacheren Formen.
              </AlertDescription>
            </Alert>
          )}

          {showOuterRadius && (
            <RadiusSlider
              id="outer-radius"
              label="Abrundung außen"
              value={design.outerBorderRadius}
              onChange={actions.setOuterBorderRadius}
            />
          )}

          {showInnerRadius && (
            <RadiusSlider
              id="inner-radius"
              label="Abrundung innen"
              value={design.innerBorderRadius}
              onChange={actions.setInnerBorderRadius}
            />
          )}

          <div className="flex items-center gap-4 border-t pt-4">
            <EyePreview
              outerStyle={design.outerEyeStyle}
              innerStyle={design.innerEyeStyle}
              color={design.eyeColor}
              background={design.backgroundColor}
              outerRadius={design.outerBorderRadius}
              innerRadius={design.innerBorderRadius}
            />
            <p className="text-sm text-muted-foreground">
              Vorschau einer Eckmarkierung in Originalproportionen.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
